import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, startWith, tap } from "rxjs/operators";

import { faHeart as farHeart } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { AsyncPipe, NgClass, NgFor, NgIf } from "@angular/common";
import { faCheck, faFilter, faHeart, faInfo, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { GetProductDetailQuery } from "../../common/gql/graphql";
import { ActiveService } from "../../../core/providers/active-order/active.service";
import { ModalService } from "../../../core/providers/modal/modal.service";
import { BaseVariantSelectorComponent } from "../base-variant-selector/base-variant-selector.component";
import { extractSizeValue } from "../../../common/utils/extract-size-value";
import { OrderService } from "../../../core/providers/order/order.service";
import { UppercaseFirstPipe } from "../../../shared/pipes/uppercase-first.pipe";
import { FormatPricePipe } from "../../../shared/pipes/format-price.pipe";
import { OnVisibleDirective } from "../../../shared/directives/on-visible/on-visible.directive";
import { StockLevelLabelComponent } from "../stock-level-label/stock-level-label.component";
import { AddToWishlistButtonComponent } from "../add-to-wishlist-button/add-to-wishlist-button.component";
import { PictureComponent } from "../../../shared/components/picture/picture.component";
import { OfferLabelComponent } from "../offer-label/offer-label.component";
import {
    ShippingInfoLabelComponent
} from "../../../shared/components/shipping-info-label/shipping-info-label.component";
import { TabbedDescriptionComponent } from "../tabbed-description/tabbed-description.component";
import { AssetGalleryComponent } from "../asset-gallery/asset-gallery.component";
import { StarRatingComponent } from "../../../shared/components/star-rating/star-rating.component";
import { BrandLabelComponent } from "../brand-label/brand-label.component";
import { SpecialPriceBadgeComponent } from "../special-price-badge/special-price-badge.component";
import { ProductAccordionComponent } from "../product-accordion/product-accordion.component";
import { StickyAnchorDirective } from "../../../shared/directives/sticky-anchor/sticky-anchor.directive";
import {
    StickyAnchorContainerDirective
} from "../../../shared/directives/sticky-anchor/sticky-anchor-container.directive";
import { qualifiesForLetterboxService } from "../../common/qualifies-for-letterbox-service";
import { LargeLetterLabelComponent } from "../large-letter-label/large-letter-label.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";

const FORMAT_NAME = 'format';

type Variant = NonNullable<GetProductDetailQuery['product']>['variants'][number];

@Component({
    selector: 'kb-product-page-color-chart',
    templateUrl: './product-page-color-chart.component.html',
    styleUrls: ['./product-page-color-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        BrandLabelComponent,
        StarRatingComponent,
        AssetGalleryComponent,
        TabbedDescriptionComponent,
        ShippingInfoLabelComponent,
        OfferLabelComponent,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        NgFor,
        NgClass,
        FontAwesomeModule,
        PictureComponent,
        AddToWishlistButtonComponent,
        StockLevelLabelComponent,
        OnVisibleDirective,
        AsyncPipe,
        FormatPricePipe,
        UppercaseFirstPipe,
        SpecialPriceBadgeComponent,
        ProductAccordionComponent,
        StickyAnchorDirective,
        StickyAnchorContainerDirective,
        LargeLetterLabelComponent,
        SpinnerComponent
    ]
})
export class ProductPageColorChartComponent
    extends BaseVariantSelectorComponent
    implements OnInit, OnChanges, OnDestroy
{
    variants$ = new BehaviorSubject<Variant[]>([]);
    filteredVariants$: Observable<Variant[]>;
    sortControl = new UntypedFormControl('name');
    filterControl = new UntypedFormControl('');
    selectedFormatOptionId = new UntypedFormControl();
    formatOptionGroup?: { id: string; options: Array<{ id: string; name: string; icon: string }> };
    sortOptions = ['name', 'colour', 'price'];
    quantities: { [id: string]: number } = {};
    addedToWishlist: { [id: string]: boolean } = {};
    processingIds$: Observable<string[]>;
    qualifiesForLetterboxService = false;
    // Used to display a "loading" state after clicking "add to cart" etc.
    private variantClicked$ = new Subject<string>();
    loadAllColors$ = new Subject<boolean>();
    private readonly displayItemsBeforeScroll = 25;
    filter = faFilter;
    check = faCheck;
    heartSolid = faHeart;
    heart = farHeart;
    minus = faMinus;
    plus = faPlus;
    info = faInfo;

    constructor(
        private modalService: ModalService,
        protected override activeService: ActiveService,
        protected override changeDetector: ChangeDetectorRef,
        orderService: OrderService,
    ) {
        super(activeService, changeDetector);
        this.processingIds$ = combineLatest([
            this.activeService.activeOrder$,
            orderService.inFlight$,
        ]).pipe(map(([order, inFlight]) => {
            return inFlight.map(item => {
                if (item.type === 'variantId') {
                    return item.id;
                } else {
                    return order?.lines.find((l) => l.id === item.id)?.productVariant.id;
                }
            }).filter(notNullOrUndefined)
        }))
    }

    override ngOnInit() {
        super.ngOnInit();
        this.filteredVariants$ = combineLatest(
            this.variants$,
            this.sortControl.valueChanges.pipe(startWith('name')),
            this.filterControl.valueChanges.pipe(startWith('')),
            this.selectedFormatOptionId.valueChanges.pipe(startWith(this.formatOptionGroup?.options[0].id)),
            this.loadAllColors$.pipe(startWith(false), distinctUntilChanged()),
        ).pipe(
            map(([variants, sortBy, filterTerm, formatOptionId, loadAllColors]) => {
                return this.filterVariants(
                    this.sortVariants(variants, sortBy),
                    filterTerm,
                    formatOptionId,
                    loadAllColors || !!filterTerm,
                );
            }),
            tap(variants => {
                this.qualifiesForLetterboxService = qualifiesForLetterboxService(variants, null);
            })
        );
    }

    override ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if (this.product) {
            this.variants$.next(this.product.variants);
            const canSortByColor = this.product.variants.some((v) => v.colorChartPosition != null);
            this.sortOptions = canSortByColor ? ['name', 'colour', 'price'] : ['name', 'price'];
        }
        const formatOptionGroup = this.product?.optionGroups.find(
            (og) => og.name.toLowerCase() === FORMAT_NAME,
        );
        if (formatOptionGroup) {
            this.formatOptionGroup = {
                ...formatOptionGroup,
                options: formatOptionGroup.options
                    .map((option) => ({
                        ...option,
                        icon: this.getIconFromFormatName(option.name),
                    }))
                    .sort((a, b) => {
                        return extractSizeValue(a.name) < extractSizeValue(b.name) ? -1 : 1;
                    }),
            };
            this.selectedFormatOptionId.setValue(
                formatOptionGroup.options.length ? formatOptionGroup.options[0].id : undefined,
            );
        } else {
            this.formatOptionGroup = undefined;
            this.selectedFormatOptionId.setValue(undefined);
        }
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
    }

    trackById(index: number, item: { id: string }) {
        return item.id;
    }

    listOptions(variant: Variant): { name: string; format: string } {
        const name = variant.options
            .filter((o) => o.groupId !== this.formatOptionGroup?.id)
            .map((o) => o.name)
            .join(' ');
        const format = variant.options
            .filter((o) => o.groupId === this.formatOptionGroup?.id)
            .map((o) => o.name)
            .join(' ');
        return { name, format };
    }

    setQuantityForVariant(variantId: string, event: Event) {
        this.quantities[variantId] = +(event.target as HTMLInputElement).value;
    }

    async displayColorInfo(variant: Variant) {
        const { VariantDetailDialogComponent } = await import(
            '../variant-detail-dialog/variant-detail-dialog.component'
        );
        this.modalService
            .fromComponent(VariantDetailDialogComponent, {
                closable: true,
                size: 'lg',
                locals: {
                    sku: variant.sku,
                },
            })
            .subscribe();
    }

    addToCart(productVariantId: string, quantity: number) {
        this.addToCartClick.emit({
            productVariantId,
            quantity,
        });
        this.variantClicked$.next(productVariantId);
    }

    setQuantity(productVariantId: string, quantity: number) {
        this.manuallySetQuantity(productVariantId, quantity);
        this.variantClicked$.next(productVariantId);
    }

    addToWishList(productVariantId: string, wishlistId: string) {
        this.addedToWishlist[productVariantId] = true;
        this.addToWishlistClick.emit({ productVariantId, wishlistId });
    }

    pageScrolled(visible: boolean) {
        if (visible) {
            this.loadAllColors$.next(true);
        }
    }

    private sortVariants(variants: Variant[], sortBy: 'name' | 'colour' | 'price'): Variant[] {
        switch (sortBy) {
            case 'name':
                return variants.slice().sort((a, b) => (a.name < b.name ? -1 : 1));
            case 'price':
                return variants.slice().sort((a, b) => a.priceWithTax - b.priceWithTax);
            case 'colour':
                return variants
                    .slice()
                    .sort((a, b) => (a.colorChartPosition ?? 0) - (b.colorChartPosition ?? 0));
        }
    }

    private filterVariants(
        variants: Variant[],
        filterTerm: string,
        formatOptionId: string | undefined,
        loadAll: boolean,
    ) {
        const sizeOptionGroup = this.formatOptionGroup;
        const filterTermLower = filterTerm.toLowerCase();
        let filtered: Variant[];
        if (sizeOptionGroup && formatOptionId) {
            filtered = variants.filter((v) => {
                const isSelectedFormat = !!v.options.find(
                    (o) => o.groupId === sizeOptionGroup.id && o.id === formatOptionId,
                );
                const matchesFilterTerm =
                    filterTerm !== ''
                        ? v.name.toLowerCase().includes(filterTermLower) ||
                          v.sku.toLowerCase().includes(filterTermLower)
                        : true;
                return isSelectedFormat && matchesFilterTerm;
            });
        } else {
            filtered = variants.filter((v) => {
                return filterTerm !== ''
                    ? v.name.toLowerCase().includes(filterTermLower) ||
                          v.sku.toLowerCase().includes(filterTermLower)
                    : true;
            });
        }
        if (loadAll) {
            return filtered;
        } else {
            return filtered.slice(0, this.displayItemsBeforeScroll);
        }
    }

    private getIconFromFormatName(formatName: string): string {
        const formatNameNormalized = formatName.toLowerCase().trim();
        if (formatNameNormalized === 'half pan') {
            return 'assets/icons/storefront-paint-format-icons-half-pan.svg';
        }
        if (formatNameNormalized === 'whole pan') {
            return 'assets/icons/storefront-paint-format-icons-whole-pan.svg';
        }
        if (formatNameNormalized.startsWith('5ml') || formatNameNormalized.startsWith('8ml')) {
            return 'assets/icons/storefront-paint-format-icons-tube-s.svg';
        }
        if (formatNameNormalized.endsWith('tub')) {
            return 'assets/icons/storefront-paint-format-icons-tub.svg';
        }
        return 'assets/icons/storefront-paint-format-icons-tube-l.svg';
    }
}
